import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import dayjs from 'dayjs';
import styled from 'styled-components';

import Button from 'components/lib/Button';
import Dropdown from 'components/lib/Dropdown';
import { isManagedBySuperuser } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';
import { SnapshotDataType } from 'types/snapshot';

import { DOUBLE_PANE_WIDTH, SINGLE_PANE_WIDTH } from './constants';
import { getSnapshotStatusLabel } from './helpers';
import SnapshotActor from './SnapshotActor';
import { useSnapshotContext } from './SnapshotContext';
import SnapshotSelectorMenu from './SnapshotSelectorMenu';
import Icon from '../Icons/Icon';

const Container = styled.div`
  display: flex;
  gap: 5px;
  justify-content: end;
`;

const Label = styled.div`
  display: flex;
  gap: 4px;
  color: var(--text-primary);
  font-size: 12px;
  align-items: center;

  &.active {
    color: var(--dark-blue);
  }
`;

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const SnapshotSelector = ({ open, setOpen }: Props) => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const { isLoading, snapshot, currentSnapshot, historicalSnapshots, draftSnapshots, setSnapshot } =
    useSnapshotContext();

  const [showSnapshots, setShowSnapshots] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<SnapshotDataType | undefined>();

  useEffect(() => {
    const allSnapshots = [
      ...(currentSnapshot ? [currentSnapshot] : []),
      ...(historicalSnapshots ?? []),
      ...(draftSnapshots ?? []),
    ];

    const item = allSnapshots?.find((item: SnapshotDataType) => item.uuid === snapshot?.uuid);
    setSelectedSnapshot(item);
  }, [snapshot]);

  const handleConfirm = () => {
    setSnapshot(selectedSnapshot);
    setOpen(false);
  };

  const handleCancel = () => {
    setSelectedSnapshot(snapshot);
    setOpen(false);
  };

  const handleToggle = (value: boolean) => {
    if (value) {
      // Check if needed to show expanded dropdown menu
      if (historicalSnapshots?.some((item: SnapshotDataType) => item.uuid === snapshot?.uuid)) {
        setShowSnapshots(true);
      }
      setOpen(value);
    } else {
      handleCancel();
    }
  };

  const dateFormat = isManagedBySuperuser(currentUser) ? 'MMM DD YYYY' : 'MMM YYYY';

  return (
    <Container>
      <Dropdown
        open={open}
        onOpenChange={handleToggle}
        disabled={isLoading || !(currentSnapshot || Boolean(draftSnapshots?.length))}
        trigger={['click']}
        placement={'bottomRight'}
        overlayStyle={{
          padding: 0,
          borderRadius: 0,
          backgroundColor: 'white',
          border: '1px solid var(--line-gray)',
          minWidth: showSnapshots ? DOUBLE_PANE_WIDTH : SINGLE_PANE_WIDTH,
        }}
        dropdownRender={(menu) => {
          return (
            <SnapshotSelectorMenu
              showSnapshots={showSnapshots}
              setShowSnapshots={setShowSnapshots}
              selectedSnapshot={selectedSnapshot}
              setSelectedSnapshot={setSelectedSnapshot}
              onConfirm={handleConfirm}
              onCancel={handleCancel}
            />
          );
        }}
      >
        <Button
          data-test-id="cd-snapshot-dropdown"
          className={classNames('dropdown', { active: Boolean(selectedSnapshot) })}
          disabled={isLoading || !(currentSnapshot || Boolean(draftSnapshots?.length))}
          loading={isLoading}
          style={{ height: 28 }}
        >
          <Label className={classNames({ active: Boolean(selectedSnapshot) })}>
            {!selectedSnapshot && !isLoading && <span>Snapshots not available</span>}
            {!selectedSnapshot && isLoading && <span>Snapshots loading...</span>}
            {!!snapshot && (
              <>
                <span style={{ fontFamily: 'var(--font-bold)' }}>Viewing {getSnapshotStatusLabel(snapshot)} data</span>
                <span>·</span>
                {snapshot && (
                  <>
                    <span>Added {dayjs(snapshot?.date).format(dateFormat)}</span>
                    <SnapshotActor snapshot={snapshot} separator={false} />
                  </>
                )}
                <Icon src={'/icons/dropdown-caret-down-blue.svg'} />
              </>
            )}
          </Label>
        </Button>
      </Dropdown>
    </Container>
  );
};

export default SnapshotSelector;
